import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { clsx } from 'clsx';
import { Button, Heading, HStack, LinkButton, Text, VStack } from '@library';
import type { ProductDownload } from '../../types';
import printUrl, { getUrl } from '../../../core/util/printUrl';
import OpenLinkIcon from '../../../core/components/icons/OpenLinkIcon';
import DownloadIcon from '../../../core/components/icons/DownloadIcon';
import styles from './ProductDetailsDownloads.module.scss';
import ProductDocumentIcon from './ProductDocumentIcon';

const Link = memo(({ url }: { url?: string }) => {
    return (
        <LinkButton href={url} target="_blank" text inline>
            <div className={styles.link}>
                <HStack justify="spaceBetween" expanded>
                    <Text>
                        <FormattedMessage id="Product ProductDetails SafetyDatasheets" />
                    </Text>

                    <HStack gap="mini">
                        <OpenLinkIcon />

                        <Text light>
                            <FormattedMessage id="Product ProductDetails OpenPage" />
                        </Text>
                    </HStack>
                </HStack>
            </div>
        </LinkButton>
    );
});

const ProductDownloadLink = memo(
    ({
        url,
        name,
        onClick,
    }: {
        url: string;
        name?: string;
        onClick?: () => void;
    }) => {
        const href = onClick ? undefined : url;

        return (
            <LinkButton
                href={href}
                target="_blank"
                text
                secondary
                inline
                onClick={onClick}
            >
                <div className={clsx(styles.link, styles.document)}>
                    <HStack justify="spaceBetween" expanded align="center">
                        <HStack align="center" gap="mini">
                            <ProductDocumentIcon />
                            <Text bold>
                                {name || (
                                    <FormattedMessage id="Product ProductDetails EmptyFileNamePlaceholder" />
                                )}
                            </Text>
                        </HStack>
                        <Button text inline>
                            <HStack gap="mini" align="center">
                                <DownloadIcon />

                                <Text light>
                                    <FormattedMessage id="Product ProductDetails Download" />
                                </Text>
                            </HStack>
                        </Button>
                    </HStack>
                </div>
            </LinkButton>
        );
    }
);

const ProductDetailsDownloads = ({
    documents,
    safetyDatasheetUrl,
}: {
    documents?: ProductDownload[];
    safetyDatasheetUrl?: string;
}) => {
    const intl = useIntl();

    const triggerPrint = () => {
        printUrl(window.location.href.replace('/product/', '/product-sheet/'), {
            auth: true,
        });
    };

    const productSheetUrl = getUrl(
        window.location.href.replace('/product/', '/product-sheet/'),
        true
    );

    return (
        <VStack gap="small">
            <HStack gap="small" expanded wrap justify="spaceBetween">
                <div className={styles.downloads}>
                    <VStack gap="mini" expanded>
                        <Heading bold h4>
                            <FormattedMessage id="Product ProductDetails Downloads" />
                        </Heading>
                        <ProductDownloadLink
                            url={productSheetUrl}
                            onClick={triggerPrint}
                            name={intl.formatMessage({
                                id: 'product data sheet',
                            })}
                        />
                        {documents?.map(({ name, url }) => {
                            return (
                                <ProductDownloadLink
                                    key={url}
                                    name={name}
                                    url={url}
                                />
                            );
                        })}
                    </VStack>
                </div>
                <div className={styles.downloads}>
                    {safetyDatasheetUrl && (
                        <VStack gap="mini" expanded>
                            <Heading bold h4>
                                Links
                            </Heading>

                            <Link url={safetyDatasheetUrl} />
                        </VStack>
                    )}
                </div>
            </HStack>
        </VStack>
    );
};

export default memo(ProductDetailsDownloads);
